<template>
  <v-dialog v-model="dialog" max-width="620" persistent>
    <v-card class="schedule-card">

      <!-- ================= HEADER ================= -->
      <div class="header">
        <div>
          <h2 class="title">Create Shift</h2>
          <p class="subtitle">
            {{ selectedGroup ? selectedGroup.name + ' — ' : '' }}{{ selectedDate }}
          </p>
        </div>
        <v-btn icon @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- ================= BODY ================= -->
      <v-card-text class="body pa-0">

        <!-- ===== RESTAURANT BLOCKS ===== -->
        <div v-for="(block, index) in form.restaurants" :key="index" class="business-row">
          <div class="business-col">
            <label class="label">Business ({{ index + 1 }})
              <v-btn icon small color="red" v-if="form.restaurants.length > 2" @click="removeRestaurant(index)"><v-icon
                  small>mdi-close</v-icon></v-btn>
            </label>
            <div class="restaurant-chip">
              <v-icon small color="#11a8c9" class="mr-1">mdi-store-outline</v-icon>
              <span class="restaurant-name">
                {{ block._restaurant_name || 'Unknown restaurant' }}
              </span>
            </div>
          </div>

          <div class="time-col">
            <label class="label">Start Time<span v-if="index === 0" class="required">*</span></label>
            <v-autocomplete v-model="block.start_time" :items="timeSlots" placeholder="--:--" outlined dense
              hide-details clearable open-on-clear prepend-inner-icon="mdi-clock-outline" :menu-props="{ auto: true, maxHeight: 300 }" @focus="onTimeFocus(block, 'start_time')"
              @blur="formatTime(block, 'start_time')" />
          </div>

          <div class="time-col">
            <label class="label">End Time<span v-if="index === 0" class="required">*</span></label>
            <v-autocomplete v-model="block.end_time" :items="timeSlots" placeholder="--:--" outlined dense hide-details
              clearable open-on-clear prepend-inner-icon="mdi-clock-outline" :menu-props="{ auto: true, maxHeight: 300 }" @focus="onTimeFocus(block, 'end_time')"
              @blur="formatTime(block, 'end_time')" />
          </div>

        </div>

        <!-- ===== DRIVER INFO ===== -->
        <div class="driver-row">
          <div class="driver-col">
            <label class="label">Driver Info</label>
            <v-autocomplete v-model="form.driver_id" :items="drivers" item-text="full_name" item-value="id"
              placeholder="Select Driver (optional)" outlined dense hide-details clearable>
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.full_name }}</v-list-item-title>
                  <v-list-item-subtitle v-if="item.phone">{{ item.phone }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </div>
          <div class="time-col">
            <label class="label">Start Time</label>
            <v-text-field :value="driverStartTime || ''" placeholder="--:--" outlined dense hide-details readonly
              disabled class="time-field disabled-field">
              <template v-slot:prepend-inner>
                <v-icon small color="#11a8c9">mdi-clock-outline</v-icon>
              </template>
            </v-text-field>
          </div>

          <div class="time-col">
            <label class="label">End Time</label>
            <v-text-field :value="driverEndTime || ''" placeholder="--:--" outlined dense hide-details readonly disabled
              class="time-field disabled-field">
              <template v-slot:prepend-inner>
                <v-icon small color="#11a8c9">mdi-clock-outline</v-icon>
              </template>
            </v-text-field>
          </div>
        </div>

        <!-- ===== SUMMARY BOXES ===== -->
        <div class="summary-row">
          <div class="summary-box">
            <div class="summary-label">Shift Length</div>
            <div class="summary-bottom">
              <span class="summary-value">{{ shiftLength }} Hours</span>
              <div class="summary-icon-wrap">
                <v-icon class="summary-icon">mdi-clock-outline</v-icon>
              </div>
            </div>
          </div>

          <div class="summary-box">
            <div class="summary-label">Shift Cost</div>
            <div class="summary-bottom">
              <span class="summary-value">£{{ shiftCost }}</span>
              <div class="summary-icon-wrap">
                <v-icon class="summary-icon">mdi-currency-gbp</v-icon>
              </div>
            </div>
          </div>
        </div>

        <!-- ===== EXTRA OPTIONS ===== -->
        <div class="extra-row">
          <div class="extra-col">
            <label class="label">Cancellation Fee</label>
            <div class="counter-box">
              <v-btn icon small @click="form.cancellation_fee = Math.max(0, form.cancellation_fee - 1)">
                <v-icon small>mdi-minus</v-icon>
              </v-btn>
              <span class="counter-value">{{ form.cancellation_fee }}</span>
              <v-btn icon small @click="form.cancellation_fee++">
                <v-icon small>mdi-plus</v-icon>
              </v-btn>
            </div>
          </div>

          <div class="extra-col">
            <label class="label">Extra Request</label>
            <v-select :items="['No', 'Yes']" v-model="form.extra_request" outlined dense hide-details />
          </div>
        </div>

        <!-- ===== COMMENT ===== -->
        <div class="comment-field">
          <label class="label">Comment</label>
          <v-textarea v-model="form.comment" placeholder="Write additional notes here..." outlined dense hide-details
            rows="3" />
        </div>

        <!-- Validation error -->
        <v-alert v-if="validationError" type="error" dense class="mt-2 mb-0">
          {{ validationError }}
        </v-alert>

      </v-card-text>

      <!-- ================= FOOTER ================= -->
      <div class="footer">
        <v-btn large outlined class="restore-btn" @click="resetForm">
          <v-icon left>mdi-restore</v-icon>
          Restore
        </v-btn>
        <v-btn large class="save-btn" :loading="$store.state.loading" @click="saveShift">
          <v-icon left>mdi-content-save-outline</v-icon>
          Save Changes
        </v-btn>
      </div>

    </v-card>
  </v-dialog>
</template>
<script>
export default {
  name: "NewDrivershareScheduleModal",

  props: {
    // "YYYY-MM-DD" string — the cell date the user clicked
    selectedDate: { type: String, default: "" },
    // The restaurantGroup object from restaurantGroupsWithShifts
    selectedGroup: { type: Object, default: null },
  },

  data() {
    return {
      dialog: true,
      validationError: null,
      form: {
        restaurants: [],
        driver_id: null,
        cancellation_fee: 0,
        extra_request: "No",
        comment: "",
      }
    }
  },
  computed: {
    drivers() {
      const all = this.$store.getters.drivers || []
      if (!this.selectedGroup || !this.selectedGroup.area_id) {
        return all
      }
      return all.filter(d => {
        return (
          d.id > 0 &&
          Array.isArray(d.area_ids) &&
          d.area_ids.some(a => a.area_id === this.selectedGroup.area_id)
        )
      })
    },
    timeSlots() {
      const slots = []
      for (let h = 0; h < 24; h++) {
        for (let m = 0; m < 60; m += 15) {
          slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`)
        }
      }
      return slots
    },
    driverStartTime() {
      const times = this.form.restaurants.map(b => b.start_time).filter(Boolean)
      if (!times.length) return null
      return times.reduce((min, t) => t < min ? t : min)
    },
    driverEndTime() {
      const times = this.form.restaurants.map(b => b.end_time).filter(Boolean)
      if (!times.length) return null
      return times.reduce((max, t) => t > max ? t : max)
    },
    shiftLength() {
      let minStart = null;
      let maxEnd = null;
      for (const block of this.form.restaurants) {
        if (!block.start_time || !block.end_time) continue;
        const [sh, sm] = block.start_time.split(':').map(Number);
        const [eh, em] = block.end_time.split(':').map(Number);
        let start = sh * 60 + sm;
        let end = eh * 60 + em;

        // Handle overnight
        if (end < start) {
          end += 24 * 60;
        }

        if (minStart === null || start < minStart) {
          minStart = start;
        }

        if (maxEnd === null || end > maxEnd) {
          maxEnd = end;
        }
      }

      if (minStart === null || maxEnd === null) return "0.00";

      return ((maxEnd - minStart) / 60).toFixed(2);
    },
    restaurantRate() {
      const count = this.form.restaurants.length || 1;

      const key = `drivershare_restaurant_count_${count}`;

      const opt = this.$store.getters.options.find(o => o.okey === key);

      return opt ? parseFloat(opt.ovalue) : 0;
    },
    shiftCost() {
      const ratePerHour = this.restaurantRate;
      const hours = parseFloat(this.shiftLength);
      let total = hours * ratePerHour;

      // Add bonuses separately
      for (const block of this.form.restaurants) {
        total += (block.cost_bonus || 0);
        total += (block.cost_extra_bonus || 0);
      }
      return total.toFixed(2);
    },
  },
  created() {
    this.initRestaurantsFromGroup()
    this.$store.dispatch('fetchOptions')
  },
  methods: {
    initRestaurantsFromGroup() {
      const restaurants = (this.selectedGroup && this.selectedGroup.restaurants) ? this.selectedGroup.restaurants : []
      if (restaurants.length > 0) {
        this.form.restaurants = restaurants.map(r => ({
          restaurant_id: r.id,
          _restaurant_name: r.name,         // display only, not sent to API
          start_time: null,
          end_time: null,
          cost_bonus: 0,
          cost_extra_bonus: 0,
          comment: null,
          startMenu: false,
          endMenu: false,
        }))
      } else {
        this.form.restaurants = [this.emptyBlock()]
      }
    },
    emptyBlock() {
      return {
        restaurant_id: null,
        start_time: null,
        end_time: null,
        cost_per_hour: 12.25,
        cost_bonus: 0,
        cost_extra_bonus: 0,
        comment: null,
        startMenu: false,
        endMenu: false,
      }
    },
    validate() {
      if (!this.selectedDate) {
        this.validationError = 'No date selected.'
        return false
      }
      if (!this.selectedGroup) {
        this.validationError = 'No restaurant group selected.'
        return false
      }
      if (!this.form.restaurants.length) {
        this.validationError = 'This group has no restaurants configured.'
        return false
      }
      // At least one restaurant must have both times set
      const hasAnyTimes = this.form.restaurants.some(b => b.start_time && b.end_time)
      if (!hasAnyTimes) {
        this.validationError = 'Please set start and end time for at least one business.'
        return false
      }
      // Any restaurant that has start_time must also have end_time and vice versa
      for (const block of this.form.restaurants) {
        if (block.start_time && !block.end_time) {
          this.validationError = `Set an end time for "${block._restaurant_name}".`
          return false
        }
        if (!block.start_time && block.end_time) {
          this.validationError = `Set a start time for "${block._restaurant_name}".`
          return false
        }
      }
      this.validationError = null
      return true
    },
    closeDialog() {
      this.dialog = false
      this.$emit('close')
    },
    resetForm() {
      this.validationError = null
      this.form = {
        restaurants: [],
        driver_id: null,
        cancellation_fee: 0,
        extra_request: "No",
        comment: "",
      }
      this.initRestaurantsFromGroup()
    },
    async saveShift() {
      if (!this.validate()) return
      const payload = {
        period_date: this.selectedDate,               // required|date
        restaurant_group_id: this.selectedGroup.id,           // required
        driver_id: this.form.driver_id || null,     // nullable

        // Only send rows where the user has filled in times
        // (restaurants without times are skipped — user left them blank intentionally)
        restaurants: this.form.restaurants
          .filter(b => b.restaurant_id && b.start_time && b.end_time)
          .map(b => ({
            restaurant_id: b.restaurant_id,                  // required
            start_time: b.start_time,                     // required H:i
            finish_time: b.end_time,                       // required H:i  ← API uses finish_time
            cost_per_hour: b.cost_per_hour,                  // required numeric
            cost_bonus: b.cost_bonus || 0,          // nullable
            cost_extra_bonus: b.cost_extra_bonus || 0,          // nullable
            comment: b.comment || null,       // nullable
          })),

        // Extra fields (not validated by API but useful to store)
        cancellation_fee: this.form.cancellation_fee,
        extra_request: this.form.extra_request,
        comment: this.form.comment,
      }

      try {
        // Action name matches the store exactly: createDrivershareShift
        await this.$store.dispatch('createDrivershareShift', payload)
        this.$emit('saved')   // tells parent to re-fetch the schedule grid
        this.closeDialog()
      } catch (e) {
        this.validationError = 'Failed to save shift. Please try again.'
        console.error('[NewDrivershareScheduleModal] saveShift error:', e)
      }
    },
    removeRestaurant(index) {
      this.form.restaurants.splice(index, 1);
    },
    formatTime(block, field) {
      let val = block[field]
      if (!val) return

      // Normalize input
      val = val.replace(/[^\d:]/g, '')

      if (!val.includes(':')) {
        if (val.length === 3) val = '0' + val
        if (val.length === 4) {
          val = val.slice(0, 2) + ':' + val.slice(2)
        }
      }

      const [h, m] = val.split(':').map(Number)

      if (isNaN(h) || isNaN(m) || h > 23 || m > 59) {
        block[field] = null
        return
      }

      block[field] =
        String(h).padStart(2, '0') +
        ':' +
        String(m).padStart(2, '0')
    },
    onTimeFocus(block, field) {
      if (!block[field]) {
        const now = new Date()

        const roundedMinutes = Math.floor(now.getMinutes() / 15) * 15

        const time =
          String(now.getHours()).padStart(2, '0') +
          ':' +
          String(roundedMinutes).padStart(2, '0')

        block[field] = time
      }
    }
  }
}
</script>

<style scoped>
.schedule-card {
  border-radius: 20px !important;
  padding: 24px;
}

/* ================= HEADER ================= */
.header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}

.title {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 2px;
}

.subtitle {
  font-size: 13px;
  color: #6b7280;
  margin: 0;
}

/* ================= LABELS ================= */
.label {
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 5px;
  display: block;
  color: #111827;
}

.required {
  color: #ef4444;
}

/* ================= ROWS ================= */
.business-row,
.driver-row {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 14px;
}
.business-col {
  flex: 1.7;
}

.time-col {
  flex: 1;
  min-width: 100px;   
}

.driver-col {
  flex: 1.7;
}
.restaurant-chip {
  display: flex;
  align-items: flex-start; 
  height: 40px;
  padding: 0 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 13px;
  color: #111827;
  background: #f9fafb;
  font-weight: 500;
  min-height: 40px;  
}

.restaurant-name {
  white-space: normal;      
  word-break: break-word;     
  line-height: 1.2;
}

/* ================= TIME FIELD ================= */
.disabled-field {
  opacity: 0.6;
}

::v-deep .disabled-field .v-input__slot {
  background: #f3f4f6 !important;
}

/* ================= TIME LIST DROPDOWN ================= */
.time-list {
  padding: 4px 0;
}

.time-list .v-list-item {
  min-height: 36px;
  font-size: 14px;
  color: #111827;
  cursor: pointer;
}

.time-list .v-list-item:hover {
  background: #f0f9fb;
}

.time-list .time-selected {
  background: #e0f5fa !important;
}

.time-list .time-selected .v-list-item__title {
  color: #11a8c9 !important;
  font-weight: 600;
}

/* ================= SUMMARY BOXES ================= */
.summary-row {
  display: flex;
  gap: 12px;
  margin: 16px 0;
}

.summary-box {
  flex: 1;
  background: #f4f6f9;
  border-radius: 14px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  border: 1px solid #e5e7eb;
}

.summary-label {
  font-size: 11px;
  color: #6b7280;
  font-weight: 500;
}

.summary-bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.summary-value {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
}

.summary-icon-wrap {
  width: 36px;
  height: 36px;
  border: 2px solid #11a8c9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.summary-icon {
  color: #11a8c9 !important;
  font-size: 18px !important;
}

/* ================= CANCELLATION FEE ================= */
.extra-row {
  display: flex;
  gap: 11px;
  margin-bottom: 10px;
}

.extra-col {
  flex: 1;
}

.counter-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 2px 8px;
  height: 40px;
  background: #fff;
}

.counter-value {
  font-size: 14px;
  font-weight: 500;
  color: #111827;
}

/* ================= COMMENT ================= */
.comment-field {
  margin-bottom: 4px;
}

/* ================= FOOTER ================= */
.footer {
  display: flex;
  gap: 12px;
  margin-top: 20px;
}

.restore-btn {
  flex: 1;
  border-radius: 30px !important;
  border-color: #d1d5db !important;
  color: #374151 !important;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
}

.save-btn {
  flex: 1;
  border-radius: 30px !important;
  background-color: #11a8c9 !important;
  color: white !important;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
  box-shadow: none !important;
}
</style>